import { IDate, IYearMonth, serialize } from '../date';
import { IBusiness } from './businessBase';
import { AlertType } from './hasRecommendOrders';

/**
 * 入荷
 * @property arrivalId - 入荷ID
 * @property amount - 入荷数量
 * @property leadTime - リードタイム
 * @property isFixOrdered - 確定発注済みかどうか（* ここでの確定発注とは、DBに登録されている発注情報のこと）
 * @property fixOrderDate - 確定発注日
 * @property crossDate - 入荷予定日
 * @property status - 発注のステータス
 * @property number - 発注番号
 * @property updateAt - 更新日時
 * @property memoText - メモ（省略可）
 */
interface IArrival extends IDate {
  arrivalId: string;
  amount: number;
  leadTime: number;
  isFixOrdered: boolean | undefined;
  fixOrderDate: IDate | undefined;
  crossDate: IDate | undefined;
  status: OrderStatus | undefined;
  memoText: string | undefined;
  number: string | undefined;
  updateAt: number | undefined;
  partialDeliveries: IArrival[] | undefined; // 分納の情報
}

/**
 * 発注のステータスを表す型
 * @description [発注ステータス - Wiki](https://github.com/lean-path1201/leanpath/wiki/%E7%99%BA%E6%B3%A8%E3%82%B9%E3%83%86%E3%83%BC%E3%82%BF%E3%82%B9)
 */
export const OrderStatus = {
  /**
   * 発注推奨なし。直近で推奨される発注がない状態。
   */
  NoRecommendation: 'NoRecommendation',
  /**
   * 推奨。入力ブロックで自動計算された推奨状態。
   */
  Recommended: 'Recommended',
  /**
   * 承認依頼。推奨から1次決定し、上長や営業に承認依頼を行う状態。
   */
  ApprovalRequested: 'ApprovalRequested',
  /**
   * 承認済（納期回答待ち）。発注数量と希望納期を確定し、納期回答待ちの状態。
   */
  ApprovedAwaitingDeliveryDate: 'ApprovedAwaitingDeliveryDate',
  /**
   * 納期確定。仕入先から納期のみ確定した状態。
   */
  DeliveryDateConfirmed: 'DeliveryDateConfirmed',
  /**
   * 数量確定。仕入先から数量のみ確定した状態。
   */
  QuantityConfirmed: 'QuantityConfirmed',
  /**
   * 分納。1つの発注に対して2回以上に分けて納入される状態。
   */
  PartialDelivery: 'PartialDelivery',
  /**
   * 確定。仕入先から納期、数量回答を受領し、確定した状態。
   */
  Confirmed: 'Confirmed',
  /**
   * 納入済。確定していた発注が納期通り納入された状態。
   */
  Delivered: 'Delivered',
  /**
   * 納期遅延。確定納期から納入遅延している状態。
   */
  DeliveryDelayed: 'DeliveryDelayed',
  /**
   * 表示終了。納入済ステータスから1ヶ月経過後、自動的にこのステータスになり、非表示となる状態。
   */
  DisplayEnded: 'DisplayEnded',
  /**
   * ステータスの取得に失敗した時しか使わない
   */
  Unknown: 'Unknown',
} as const;

/**
 * 発注のステータスを表す型
 */
export type OrderStatus = (typeof OrderStatus)[keyof typeof OrderStatus];

/**
 * stringをOrderStatusに変換する
 * 変換に失敗した際はOrderStatus.Unknownを返す
 * @param value - 値
 * @returns - OrderStatusの値
 */
export const stringToOrderStatusSafe = (value: string): OrderStatus => {
  if (typeof value !== 'string') return OrderStatus.Unknown;
  return OrderStatus[value] ?? OrderStatus.Unknown;
};

/*
 * 入荷のステータスを表す型。OrderStatusからの変換を想定している。
 * @property Unpaid - 未納
 * @property ApprovalRequested - 承認依頼中
 * @property Recommended - 推奨発注が存在する
 * @property Nothing - 入荷として表す状態がない
 */
export const ArrivalStatus = {
  Unpaid: 'Unpaid',
  ApprovalRequested: 'ApprovalRequested',
  Recommended: 'Recommended',
  Nothing: 'Nothing',
};

/**
 * 入荷のステータスを表す型。OrderStatusからの変換を想定している。
 * @property Unpaid - 未納
 * @property ApprovalRequested - 承認依頼中
 * @property Recommended - 推奨発注が存在する
 * @property Nothing - 入荷として表す状態がない
 */
export type ArrivalStatus = (typeof ArrivalStatus)[keyof typeof ArrivalStatus];

/**
 * 与えられた文字列キーに基づいてOrderStatusから値を取得し、見つからない場合はnullを返す関数
 * @param key - OrderStatusのキー
 */
export function findOrderStatusOrNull(key: string): OrderStatus | null {
  if (key in OrderStatus) {
    return OrderStatus[key as keyof typeof OrderStatus];
  }
  return null;
}

export const arrivalsProperties = ['arrivals'] as const;

/**
 * 入荷をまとめた型
 */
interface Arrivals {
  businessId: string;
  arrivals: IArrival[];
}

/**
 * これArrivalのオブジェクト？ってのを確認する
 * @param value - 任意の値
 */
function isArrivals(value: any): value is Arrivals {
  return (
    Object.prototype.hasOwnProperty.call(value, 'businessId') &&
    typeof value.businessId === 'string' &&
    Object.prototype.hasOwnProperty.call(value, 'arrivals') &&
    Array.isArray(value.arrivals)
  );
}
/**
 * businessIdを持っているobjectに対して、
 * Arrivals型を部分型として持つようにする。
 * @param value - 任意の値
 */
function initArrivals(value: Partial<Arrivals> & { businessId: string }): asserts value is Arrivals {
  value.arrivals = [];
}

/**
 * 入荷のヘッダーを取得する
 * @param businesses - 取引情報
 */
export function getArrivalHeaders(businesses: IBusiness[]): IArrival[] {
  const map = new Map<string, IArrival>();
  for (let i = 0; i < businesses.length; i++) {
    const targetArrival = businesses[i]?.arrivals ?? [];
    for (let j = 0; j < targetArrival.length; j++) {
      map.set(serialize(targetArrival[j]), targetArrival[j]);
    }
  }
  return Array.from(map.values()).sort((a, b) => a.year * 1000 + a.month - (b.year * 1000 + b.month));
}
/**
 * targetがgetArrivalHeadersで取得できるheaderと同一かどうかを調べる
 * @param target - 検証対象
 * @param arrivalHeaders - 検証対象
 */
export function assertArrival(target: IYearMonth, arrivalHeaders: IYearMonth[]): boolean {
  // 実装時点では、線形探索(O(N))でも大丈夫なデータ構造の想定
  // 実際に実行される計算量はbusinessesをMとした場合、M*2*O(N) => O(N*M)
  return arrivalHeaders.map((a) => serialize(a)).includes(serialize(target));
}

/**
 * Arrivalsを初期化する
 * @param businessId - 取引ID
 */
export function getinitializedArrivals(businessId: string): Arrivals {
  const value = { businessId: businessId };
  initArrivals(value);
  return value;
}
/**
 * Arrivalsを部分型として持っていることを保証する
 * @param value - 任意の値
 */
function assertParticalArrivals<T>(value: T): asserts value is T & Arrivals {
  if (!isArrivals(value)) {
    throw Error('Arrivalsを部分型として持っていません');
  }
}

export { IArrival, Arrivals, isArrivals, initArrivals, assertParticalArrivals };
